import type { Block, Blocks } from '@w/content-blocks'
import type { Edit, Json } from '@w/crdt'
import { indexBlocks } from './doc-blocks'
import { getTextDiffWindow } from './text-window'
import { deepEqual } from 'fast-equals'

/** Lower authored block containers to native field and membership operations.
 * Clean top-level views are retained by the caller and never visited here. */
export function writeDocument(tx: Edit, blocks: Blocks, previous: Blocks): void {
  const old = new Map(previous.map(block => [block.id, block]))
  writeList(tx, 'content.children', blocks.filter(block => block.type !== 'pageTitle'), old)
  const title = blocks.find(block => block.type === 'pageTitle')
  if (title?.type === 'pageTitle' && tx.read('title') !== title.title) tx.set('', 'title', title.title)
}

/**
 * A view projection in the stored leaf form: an unformatted `chars` leaf whose stored leaf is a `text` leaf
 * with the same id and text is that stored leaf, so equal content compares equal and writes nothing.
 */
export function inStoredForm<T>(value: T, stored: Blocks): T {
  const texts = new Map<string, Block>()
  const collect = (node: unknown): void => {
    if (Array.isArray(node)) { node.forEach(child => { collect(child) }); return }
    if (!node || typeof node !== 'object') return
    const block = node as Block
    if (block.type === 'text' && block.id) texts.set(block.id, block)
    Object.values(node).forEach(child => { collect(child) })
  }
  collect(stored)
  if (texts.size === 0) return value
  const adopt = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(child => adopt(child))
    if (!node || typeof node !== 'object') return node
    const block = node as Block
    if (block.type === 'chars' && !block.format) {
      const text = texts.get(block.id)
      if (text?.type === 'text' && text.text === block.text.map(part => part.text).join('')) return text
    }
    return Object.fromEntries(Object.entries(node).map(([key, child]) => [key, adopt(child)]))
  }
  return adopt(value) as T
}

function writeList(tx: Edit, path: string, blocks: Blocks, clean = new Map<string, Block>()): void {
  const targetIds = new Set(blocks.map(block => block.id).filter(Boolean))
  const current = tx.read(path)
  if (!Array.isArray(current)) tx.set(path.slice(0, path.lastIndexOf('.')), path.slice(path.lastIndexOf('.') + 1), [])
  const currentBlocks = Array.isArray(current) ? current : []
  for (let i = currentBlocks.length - 1; i >= 0; i -= 1) {
    const block = currentBlocks[i]
    if (block && typeof block === 'object' && !Array.isArray(block) &&
      typeof block.id === 'string' && !targetIds.has(block.id)) tx.remove(path, i, 1)
  }
  for (const [index, block] of blocks.entries()) {
    const location = block.id ? locations(tx).get(block.id) : undefined
    if (!location) {
      const retained = block.id ? tx.readItem(block.id) : undefined
      if (retained) {
        tx.insertRetained(path, index, block.id)
        writeBlock(tx, `${path}.${block.id}`, block)
      } else {
        const nested: [string, Blocks][] = []
        const nestedLists: [string, Blocks[]][] = []
        const fields: Record<string, Json> = {}
        for (const [key, value] of Object.entries(block)) {
          if (key === 'id' || key.startsWith('$') || value === undefined) continue
          if (isNestedBlockList(value) && value.length > 0) {
            nestedLists.push([key, value])
            fields[key] = []
          } else if (key !== 'text' && isBlockList(value) && value.length > 0) {
            nested.push([key, value])
            fields[key] = []
          } else fields[key] = value as Json
        }
        const [id] = tx.insert(path, index, [fields])
        block.id = id
        for (const [key, list] of nested) writeList(tx, `${path}.${id}.${key}`, list)
        for (const [key, lists] of nestedLists) writeNestedLists(tx, `${path}.${id}.${key}`, lists)
        if (block.type === 'chars') block.text = tx.read(`${path}.${id}.text`) as typeof block.text
      }
      continue
    }
    if (location.list !== path || location.index !== index) tx.moveTo(location.list, location.index, path, index)
    if (clean.get(block.id) === block) continue
    writeBlock(tx, `${path}.${block.id}`, block)
  }
}

const indexed = new WeakMap<Edit, { ops: number; locations: ReturnType<typeof indexBlocks> }>()

/** Block locations in the draft; re-indexed only after the transaction emitted an operation (every write appends one). */
function locations(tx: Edit): ReturnType<typeof indexBlocks> {
  const cached = indexed.get(tx)
  if (cached?.ops === tx.ops.length) return cached.locations
  const fresh = { ops: tx.ops.length, locations: indexBlocks(tx.read('') ?? null) }
  indexed.set(tx, fresh)
  return fresh.locations
}

function writeBlock(tx: Edit, path: string, block: Block): void {
  const previous = tx.read(path) as Record<string, Json>
  if (block.type === 'chars' && previous.type === 'text') {
    // A stored `text` leaf renders as a Lexical TextNode, which projects as `chars`: unformatted it stays
    // a `text` leaf, so rendering writes nothing; formatting turns it into a real `chars` leaf.
    const text = block.text.map(part => part.text).join('')
    if (!block.format) {
      if (previous.text !== text) tx.set(path, 'text', text)
      return
    }
    tx.set(path, 'type', 'chars')
    tx.replaceContainer(path, 'text', block.text.map(part => ({ id: part.id, text: part.text })))
    tx.set(path, 'format', block.format)
    return
  }
  const fields = block as unknown as Record<string, Json | undefined>
  for (const key of Object.keys(previous)) {
    if (key !== 'id' && !Object.hasOwn(fields, key)) tx.delete(path, key)
  }
  for (const [key, value] of Object.entries(fields)) {
    if (key === 'id' || key.startsWith('$') || value === undefined) continue
    if (block.type === 'chars' && key === 'text') {
      const oldText = Array.isArray(previous.text) ? previous.text.map(part =>
        part && typeof part === 'object' && !Array.isArray(part) && typeof part.text === 'string' ? part.text : '').join('') : ''
      const text = block.text.map(part => part.text).join('')
      if (oldText !== text) {
        const { prefix, suffix } = getTextDiffWindow(oldText, text)
        tx.editText(`${path}.text`, prefix, oldText.length - prefix - suffix, text.slice(prefix, text.length - suffix))
      }
    } else if (isNestedBlockList(value) && value.length > 0) {
      writeNestedLists(tx, `${path}.${key}`, value)
    } else if (isBlockList(value) && (value.length > 0 || isBlockList(previous[key]))) {
      writeList(tx, `${path}.${key}`, value)
    } else if (!deepEqual(previous[key], value)) tx.set(path, key, value)
  }
}

function writeNestedLists(tx: Edit, path: string, lists: Blocks[]): void {
  const current = tx.read(path)
  const count = Array.isArray(current) ? current.length : 0
  if (!Array.isArray(current)) tx.set(path.slice(0, path.lastIndexOf('.')), path.slice(path.lastIndexOf('.') + 1), [])
  if (count > lists.length) tx.remove(path, lists.length, count - lists.length)
  if (count < lists.length) tx.insertLists(path, count, lists.slice(count).map(() => []))
  lists.forEach((blocks, index) => { writeList(tx, `${path}.[${index}]`, blocks) })
}

function isNestedBlockList(value: unknown): value is Blocks[] {
  return Array.isArray(value) && value.every(item => isBlockList(item))
}

function isBlockList(value: unknown): value is Blocks {
  return Array.isArray(value) && value.every((block: unknown) => block !== null && typeof block === 'object' && 'type' in block)
}
